import { useRouterState } from "@tanstack/react-router";
import { getEpisode } from "@/lib/podcast-episodes";
import { keywordHubs } from "@/lib/keyword-hubs";

const SITE_ORIGIN = "https://rajshamani.life";

// Every keyword landing page gets its breadcrumb label straight from the
// shared keyword index, so the JSON-LD name always matches the visible
// breadcrumb, the hub widget and the sitemap.
const KEYWORD_HUB_LABELS: Record<string, string> = Object.fromEntries(
  keywordHubs.map((h) => [h.to.replace(/^\//, ""), h.title]),
);

// Human-readable labels for path segments. Anything not listed falls back to
// a title-cased version of the slug itself.
const SEGMENT_LABELS: Record<string, string> = {
  about: "About",
  book: "Book",
  "build-dont-talk": "Build, Don't Talk",
  contact: "Contact",
  "failure-resume": "Failure Resume",
  faq: "FAQ",
  guest: "Be a Guest",
  guides: "Guides",
  "d2c-brands-india": "D2C Brands in India",
  "how-to-start-a-podcast-india": "How to Start a Podcast in India",
  podcast: "Podcast",
  ventures: "Ventures",
  ...KEYWORD_HUB_LABELS,
  // Explicit override: keep the JSON-LD name identical to the visible
  // breadcrumb label rendered on the page.
  "raj-shamani-family": "Raj Shamani Family",
};

// Segments that are grouping prefixes with no page of their own. Emitting a
// ListItem for them would point Google at a URL that 404s.
const NON_ROUTE_SEGMENTS = new Set(["guides"]);

function labelFor(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  // Decode in case the segment is URL-encoded.
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // keep raw
  }
  return decoded
    .split("-")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

function normalizePath(pathname: string): string {
  const clean = pathname.split("?")[0].split("#")[0];
  // Strip trailing slash (except root) so canonical URLs match sitemap entries.
  if (clean.length > 1 && clean.endsWith("/")) return clean.slice(0, -1);
  return clean;
}

function buildItems(pathname: string) {
  const clean = normalizePath(pathname);
  const segments = clean.split("/").filter(Boolean);

  const items: Array<{
    "@type": "ListItem";
    position: number;
    name: string;
    item: string;
  }> = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_ORIGIN}/`,
    },
  ];

  let acc = "";
  segments.forEach((seg, idx) => {
    acc += `/${seg}`;
    if (NON_ROUTE_SEGMENTS.has(seg)) return;

    // Podcast episode pages: use the real guest name so the breadcrumb in
    // search results reads "Home > Podcast > MS Dhoni", not a slug.
    let name = labelFor(seg);
    if (idx > 0 && segments[0] === "podcast") {
      const episode = getEpisode(seg);
      if (episode) name = episode.g;
    }

    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name,
      item: `${SITE_ORIGIN}${acc}`,
    });
  });

  return items;
}


export function DynamicBreadcrumbJsonLd() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const clean = normalizePath(pathname);

  // No breadcrumb needed on the homepage — a single "Home" item is noise
  // and Google ignores single-item BreadcrumbLists.
  if (clean === "/" || clean === "") return null;

  const items = buildItems(clean);
  const json = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    // Stable, page-unique id so this list can be referenced from the page's
    // WebPage node without ever colliding with another route's breadcrumb.
    "@id": `${SITE_ORIGIN}${clean}#breadcrumb`,
    name: `${items[items.length - 1]?.name ?? "Home"} breadcrumb`,
    numberOfItems: items.length,
    itemListElement: items,
  };

  // Render inline so the markup is present in SSR HTML and visible to
  // crawlers, tests, and view-source — not just after client hydration.
  // Unicode-escape <, >, & so a path segment containing "</script>" can't
  // break out of the script tag (JSON.stringify alone does not escape these).
  const safeJson = JSON.stringify(json)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: safeJson }}
    />
  );
}
